import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { format, differenceInCalendarDays } from 'date-fns'
import { DndContext, DragOverlay, PointerSensor, closestCenter, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronRight, GripVertical, MoreHorizontal, Plus, Zap, ListChecks } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import ProjectLayout from '@/components/ProjectCentral/ProjectLayout'
import TaskTypeIcon from '@/components/ProjectCentral/TaskTypeIcon'
import { LabelChip } from '@/components/ProjectCentral/LabelsInput'
import { SprintFormDialog, CompleteSprintDialog } from '@/components/ProjectCentral/SprintDialogs'
import { sprintService } from '@/services/sprintService'
import { taskService } from '@/services/taskService'
import { projectService } from '@/services/projectService'
import { cn } from '@/lib/utils'

const personName = (u) => (u?.employee ? `${u.employee.first_name} ${u.employee.last_name || ''}`.trim() : u?.email)
const statusStyle = {
  todo: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
}
const dateRange = (s) => (s.start_date ? `${format(new Date(`${s.start_date}T00:00:00`), 'MMM d')} – ${s.end_date ? format(new Date(`${s.end_date}T00:00:00`), 'MMM d') : '?'}` : null)
const containerOf = (sprintId) => (sprintId ? `sprint-${sprintId}` : 'backlog')

// One draggable issue row
const BacklogRow = ({ task, projectKey, containerId, moveTargets, onOpen, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.id}`,
    data: { type: 'row', task, containerId }
  })
  const epic = task.parent?.type?.hierarchy_level === 1 ? task.parent : null
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('group flex items-center gap-2 border-b last:border-b-0 bg-background px-2 py-1.5 text-sm', isDragging && 'opacity-40')}
      data-task-row={task.id}
    >
      <button type="button" className="cursor-grab text-muted-foreground/50 hover:text-muted-foreground" aria-label={`Drag ${projectKey}-${task.task_number}`} {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4" />
      </button>
      <TaskTypeIcon type={task.type} />
      <button type="button" onClick={() => onOpen(task)} className="font-mono text-[11px] text-muted-foreground hover:text-primary shrink-0">
        {projectKey}-{task.task_number}
      </button>
      <button type="button" onClick={() => onOpen(task)} className={cn('flex-1 truncate text-left hover:text-primary', task.status?.category === 'done' && 'line-through text-muted-foreground')}>
        {task.title}
      </button>
      {epic && (
        <span className="hidden md:inline-flex items-center gap-0.5 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 max-w-[140px] truncate" title={epic.title}>
          <Zap className="h-3 w-3 shrink-0" /> {epic.title}
        </span>
      )}
      <span className="hidden lg:flex gap-1">{(task.labels || []).slice(0, 2).map(l => <LabelChip key={l} label={l} className="text-[10px]" />)}</span>
      {task.childCount > 0 && (
        <span className="hidden sm:inline-flex items-center gap-0.5 text-[11px] text-muted-foreground"><ListChecks className="h-3 w-3" />{task.childDoneCount}/{task.childCount}</span>
      )}
      <span className={cn('rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap', statusStyle[task.status?.category] || statusStyle.todo)}>{task.status?.name}</span>
      <span title={task.assignee ? personName(task.assignee) : 'Unassigned'}>
        <Avatar className="h-6 w-6">
          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{task.assignee ? personName(task.assignee).charAt(0).toUpperCase() : '–'}</AvatarFallback>
        </Avatar>
      </span>
      <span className="min-w-[28px] rounded-full bg-muted px-1.5 text-center text-[11px] tabular-nums" title="Story points">{task.story_points ?? '–'}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-60 group-hover:opacity-100" aria-label={`Actions for ${projectKey}-${task.task_number}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Move to</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {moveTargets.filter(t => t.container !== containerId).map(t => (
                <DropdownMenuItem key={t.container} onClick={() => onMove(task, t.sprintId)}>{t.label}</DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => onMove(task, task.sprint_id ?? null, 'top')}>Move to top</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onOpen(task)}>Open</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Quick "+ Create issue" at the bottom of a sprint or the backlog
const QuickCreate = ({ onCreate }) => {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex w-full items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
        <Plus className="h-3.5 w-3.5" /> Create issue
      </button>
    )
  }
  return (
    <form
      className="flex items-center gap-2 px-2 py-1.5"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!title.trim()) return
        await onCreate(title.trim())
        setTitle('')
      }}
    >
      <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to be done?" className="h-8 text-sm"
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setTitle('') } }} />
      <Button type="submit" size="sm" className="h-8" disabled={!title.trim()}>Add</Button>
    </form>
  )
}

// A sprint or the backlog, as a drop zone with sortable rows
const Section = ({ id, header, tasks, projectKey, moveTargets, onOpen, onMove, onCreate, emptyText }) => {
  const [collapsed, setCollapsed] = useState(false)
  const { setNodeRef, isOver } = useDroppable({ id: `drop-${id}`, data: { type: 'section', containerId: id } })
  return (
    <section className="rounded-lg border bg-muted/20" aria-label={typeof header.title === 'string' ? header.title : undefined}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setCollapsed(c => !c)} className="text-muted-foreground" aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-sm">{header.title}</h2>
            {header.badge}
            {header.meta && <span className="text-xs text-muted-foreground">{header.meta}</span>}
          </div>
          {header.goal && <p className="text-xs text-muted-foreground mt-0.5">{header.goal}</p>}
        </div>
        <span className="text-xs text-muted-foreground">{tasks.length} {tasks.length === 1 ? 'issue' : 'issues'}</span>
        {header.points}
        {header.actions}
      </div>
      {!collapsed && (
        <div ref={setNodeRef} className={cn('mx-2 mb-2 min-h-[44px] rounded-md border bg-background', isOver && 'ring-2 ring-primary/30')}>
          <SortableContext id={id} items={tasks.map(t => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
            {tasks.map(task => (
              <BacklogRow key={task.id} task={task} projectKey={projectKey} containerId={id} moveTargets={moveTargets} onOpen={onOpen} onMove={onMove} />
            ))}
          </SortableContext>
          {!tasks.length && <p className="px-3 py-3 text-xs text-muted-foreground">{emptyText}</p>}
          <div className="border-t"><QuickCreate onCreate={onCreate} /></div>
        </div>
      )}
    </section>
  )
}

const PointsBadge = ({ done, total }) => (
  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] tabular-nums" title="Story points done / total">{done} / {total} pts</span>
)

// /projects/:id/backlog — plan sprints (Jira's Scrum backlog)
const ProjectBacklog = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [dragging, setDragging] = useState(null)
  const [formDialog, setFormDialog] = useState(null) // { mode: 'start' | 'edit', sprint }
  const [completing, setCompleting] = useState(null)
  const [showClosed, setShowClosed] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const { data: projectData } = useQuery(['projects', id], () => projectService.getProject(id))
  const project = projectData?.data?.project
  const backlogKey = ['projects', String(id), 'backlog']
  const { data, isLoading } = useQuery(backlogKey, () => sprintService.getBacklog(id))
  const sprints = data?.data?.sprints || []
  const backlog = data?.data?.backlog || { tasks: [], stats: { issues: 0, points: 0 } }
  const closed = data?.data?.closed || []
  const active = sprints.find(s => s.state === 'active')
  const planned = sprints.filter(s => s.state === 'planned')

  const refresh = () => {
    queryClient.invalidateQueries(backlogKey)
    queryClient.invalidateQueries(['projects', String(id), 'sprints'])
    queryClient.invalidateQueries(['projects', String(id), 'tasks'])
  }
  const fail = (err, fallback) => toast.error(err.response?.data?.error || fallback)
  const listOf = (container) => (container === 'backlog' ? backlog.tasks : sprints.find(s => containerOf(s.id) === container)?.tasks || [])

  const move = async (taskIds, sprintId, beforeTaskId) => {
    try {
      await sprintService.moveTasks(id, taskIds, sprintId, beforeTaskId)
    } catch (err) {
      fail(err, 'Could not move the issue')
    }
    refresh()
  }

  const moveTargets = [
    ...sprints.map(s => ({ container: containerOf(s.id), sprintId: s.id, label: s.name })),
    { container: 'backlog', sprintId: null, label: 'Backlog' }
  ]

  const handleMenuMove = (task, sprintId, where) => {
    const list = listOf(containerOf(sprintId))
    const beforeTaskId = where === 'top' ? list.find(t => t.id !== task.id)?.id : undefined
    move([task.id], sprintId, beforeTaskId)
  }

  const handleDragEnd = ({ active: dragged, over }) => {
    setDragging(null)
    if (!over) return
    const task = dragged.data.current.task
    const from = dragged.data.current.containerId
    const to = over.data.current?.containerId
    if (!to) return
    const list = listOf(to)
    let beforeTaskId
    if (over.data.current.type === 'row') {
      const overTask = over.data.current.task
      if (overTask.id === task.id) return
      const overIndex = list.findIndex(t => t.id === overTask.id)
      const fromIndex = list.findIndex(t => t.id === task.id)
      // Moving down inside the same list lands after the row it's dropped on
      beforeTaskId = from === to && fromIndex >= 0 && fromIndex < overIndex ? list[overIndex + 1]?.id : overTask.id
    }
    move([task.id], to === 'backlog' ? null : Number(to.replace('sprint-', '')), beforeTaskId)
  }

  const createIn = (sprintId) => async (title) => {
    try {
      await taskService.createTask(id, { title, ...(sprintId && { sprint_id: sprintId }) })
      refresh()
    } catch (err) {
      fail(err, 'Could not create the issue')
    }
  }

  const createSprint = async () => {
    try {
      const res = await sprintService.createSprint(id)
      toast.success(`${res.data.sprint.name} created`)
      refresh()
    } catch (err) {
      fail(err, 'Could not create a sprint')
    }
  }

  const deleteSprint = async (sprint) => {
    if (!window.confirm(`Delete ${sprint.name}? Its issues go back to the backlog.`)) return
    try {
      await sprintService.deleteSprint(id, sprint.id)
      refresh()
    } catch (err) {
      fail(err, 'Could not delete the sprint')
    }
  }

  const submitForm = async (values) => {
    try {
      if (formDialog.mode === 'start') {
        await sprintService.startSprint(id, formDialog.sprint.id, values)
        toast.success(`${values.name} started`)
      } else {
        await sprintService.updateSprint(id, formDialog.sprint.id, values)
      }
      refresh()
    } catch (err) {
      fail(err, 'Could not save the sprint')
      throw err
    }
  }

  const submitComplete = async (moveTo) => {
    try {
      const res = await sprintService.completeSprint(id, completing.id, moveTo)
      const s = res.data.summary
      toast.success(`${completing.name} completed: ${s.completed_issues} done (${s.completed_points} pts)${s.carried_over_issues ? `, ${s.carried_over_issues} moved to ${s.moved_to === 'backlog' ? 'the backlog' : s.moved_to.name}` : ''}`)
      refresh()
    } catch (err) {
      fail(err, 'Could not complete the sprint')
      throw err
    }
  }

  const openTask = (task) => navigate(`/projects/${id}/tasks/${task.id}`)

  const sprintHeader = (s) => {
    const daysLeft = s.state === 'active' && s.end_date ? differenceInCalendarDays(new Date(`${s.end_date}T00:00:00`), new Date()) : null
    return {
      title: s.name,
      badge: s.state === 'active' ? <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">ACTIVE</span> : null,
      meta: [dateRange(s), daysLeft !== null ? (daysLeft >= 0 ? `${daysLeft} days left` : `${-daysLeft} days over`) : null].filter(Boolean).join(' · '),
      goal: s.goal,
      points: <PointsBadge done={s.stats.done_points} total={s.stats.points} />,
      actions: (
        <div className="flex items-center gap-1">
          {s.state === 'active'
            ? <Button size="sm" className="h-7" onClick={() => setCompleting(s)}>Complete sprint</Button>
            : <Button size="sm" variant="outline" className="h-7" disabled={!!active} title={active ? `Complete ${active.name} first` : undefined} onClick={() => setFormDialog({ mode: 'start', sprint: s })}>Start sprint</Button>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`${s.name} options`}><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFormDialog({ mode: 'edit', sprint: s })}>Edit sprint</DropdownMenuItem>
              {s.state === 'planned' && <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => deleteSprint(s)}>Delete sprint</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  }

  return (
    <ProjectLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">Drag issues between sprints and the backlog to plan, or use an issue's ⋯ menu.</p>
          <Button size="sm" variant="outline" onClick={createSprint}><Plus className="h-4 w-4 mr-1" /> Create sprint</Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">{[0, 1].map(i => <Skeleton key={i} className="h-32 w-full rounded-lg" />)}</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={({ active: a }) => setDragging(a.data.current.task)} onDragEnd={handleDragEnd} onDragCancel={() => setDragging(null)}>
            <div className="space-y-3">
              {sprints.map(s => (
                <Section key={s.id} id={containerOf(s.id)} header={sprintHeader(s)} tasks={s.tasks} projectKey={project?.key}
                  moveTargets={moveTargets} onOpen={openTask} onMove={handleMenuMove} onCreate={createIn(s.id)}
                  emptyText="Plan this sprint by dragging issues here from the backlog." />
              ))}
              <Section
                id="backlog"
                header={{ title: 'Backlog', points: <PointsBadge done={0} total={backlog.stats.points} /> }}
                tasks={backlog.tasks}
                projectKey={project?.key}
                moveTargets={moveTargets}
                onOpen={openTask}
                onMove={handleMenuMove}
                onCreate={createIn(null)}
                emptyText="Your backlog is empty."
              />
            </div>
            <DragOverlay>
              {dragging && (
                <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
                  <TaskTypeIcon type={dragging.type} />
                  <span className="font-mono text-[11px] text-muted-foreground">{project?.key}-{dragging.task_number}</span>
                  <span className="truncate">{dragging.title}</span>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}

        {closed.length > 0 && (
          <div className="rounded-lg border">
            <button type="button" onClick={() => setShowClosed(v => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium">
              {showClosed ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Completed sprints ({closed.length})
            </button>
            {showClosed && (
              <ul className="divide-y border-t text-sm">
                {closed.map(s => (
                  <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-xs text-muted-foreground">{dateRange(s)}{s.completed_at ? ` · completed ${format(new Date(s.completed_at), 'MMM d')}` : ''}</span>
                    {s.summary && (
                      <span className="text-xs text-muted-foreground">
                        {s.summary.completed_issues} done ({s.summary.completed_points} pts) · {s.summary.carried_over_issues} carried over ({s.summary.carried_over_points} pts)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <SprintFormDialog open={!!formDialog} mode={formDialog?.mode} sprint={formDialog?.sprint} onOpenChange={(o) => !o && setFormDialog(null)} onSubmit={submitForm} />
      <CompleteSprintDialog open={!!completing} sprint={completing} plannedSprints={planned} onOpenChange={(o) => !o && setCompleting(null)} onSubmit={submitComplete} />
    </ProjectLayout>
  )
}

export default ProjectBacklog
